import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {createPaymentSession} from 'api/customer/payment-session.api';
import JSDB from 'fromKotlin/nk';

interface Props {
    open: boolean;
    clientId: number;
    clientName: string;
    debt: number;
    invoiceIds?: number[];
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    onClose: () => void;
}

const PaymentLinkDialog = ({
    open, clientId, clientName, debt, invoiceIds, email, phone, address, city, onClose,
}: Props) => {
    const [amount, setAmount] = React.useState(Math.max(0, debt));
    const [paymentPlan, setPaymentPlan] = React.useState(12);
    const [link, setLink] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setAmount(Math.max(0, debt));
        setPaymentPlan(12);
        setLink('');
        setError('');
        setCopied(false);
    }, [open, debt, clientId]);

    const generate = async () => {
        if (!Number.isFinite(amount) || amount <= 0) {
            setError('סכום חייב להיות גדול מ-0');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const session = await createPaymentSession(amount, {
                purpose: 'payment_link',
                ref: String(clientId),
                client_id: clientId,
                invoice_ids: invoiceIds,
                contact: clientName,
                company: clientName,
                email,
                phone,
                address,
                city,
                payment_plan: paymentPlan,
            });
            setLink(session.checkout_url || session.iframe_url || '');
        } catch (e: any) {
            setError(e?.message || 'יצירת קישור התשלום נכשלה');
        } finally {
            setLoading(false);
        }
    };

    const copy = async () => {
        if (!link) return;
        await navigator.clipboard.writeText(link);
        setCopied(true);
    };

    const sendWhatsapp = () => {
        if (!link) return;
        const validPhone = phone ? JSDB().getValidPhone(phone) : '';
        let merchant = '';
        try {
            merchant = String(
                JSDB().getUser?.()?.first?.user_company_print_name
                || JSDB().getUser?.()?.first?.user_company_name
                || '',
            ).trim();
        } catch {
            merchant = '';
        }
        const payLine = merchant
            ? `לתשלום מאובטח ל${merchant} בסך ₪${amount.toFixed(2)}:`
            : `לתשלום מאובטח בסך ₪${amount.toFixed(2)}:`;
        const text = encodeURIComponent(
            `שלום ${clientName},\n${payLine}\n${link}`,
        );
        const target = validPhone
            ? `https://api.whatsapp.com/send?phone=${validPhone}&text=${text}`
            : `https://api.whatsapp.com/send?text=${text}`;
        window.open(target, '_blank')?.focus();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
            <DialogTitle>יצירת קישור תשלום — {clientName}</DialogTitle>
            <DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
                <TextField
                    label="סכום לתשלום"
                    type="number"
                    value={amount}
                    onChange={(event) => setAmount(Number(event.target.value) || 0)}
                    inputProps={{min: 0.01, step: 0.01}}
                    InputProps={{startAdornment: <InputAdornment position="start">₪</InputAdornment>}}
                    fullWidth
                />
                <FormControl fullWidth>
                    <InputLabel id="pay-link-installments-label">תשלומים (מקסימום בקישור)</InputLabel>
                    <Select
                        labelId="pay-link-installments-label"
                        label="תשלומים (מקסימום בקישור)"
                        value={paymentPlan}
                        onChange={(e) => setPaymentPlan(Number(e.target.value))}
                    >
                        {Array.from({length: 12}, (_, i) => i + 1).map((n) => (
                            <MenuItem key={n} value={n}>
                                {n === 1 ? 'תשלום אחד בלבד' : `עד ${n} תשלומים`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                    יתרת הלקוח: ₪{debt.toFixed(2)}. לאחר אישור התשלום תופק קבלה אוטומטית ותשויך
                    {invoiceIds?.length
                        ? ' לחשבוניות שסומנו בלבד, לפי הסדר.'
                        : ' לחשבוניות הפתוחות לפי הסדר.'}
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                {link && (
                    <>
                        <Alert severity="success">
                            קישור קצר נוצר. שלחו אותו ללקוח — הסכום קבוע ואחרי התשלום תופק קבלה.
                        </Alert>
                        <TextField value={link} inputProps={{dir: 'ltr', readOnly: true}} fullWidth />
                        <Box sx={{display: 'flex', gap: 1}}>
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => void copy()}
                                fullWidth
                            >
                                {copied ? 'הועתק' : 'העתק קישור'}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<WhatsAppIcon />}
                                onClick={sendWhatsapp}
                                fullWidth
                            >
                                שלח ב-WhatsApp
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">סגור</Button>
                {!link && (
                    <Button
                        variant="contained"
                        onClick={() => void generate()}
                        disabled={loading || amount <= 0}
                        startIcon={loading ? <CircularProgress size={16} /> : undefined}
                    >
                        צור קישור
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PaymentLinkDialog;
